import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const checks = [pw.length >= 8, /[a-zA-Z]/.test(pw), /\d/.test(pw)];
  const ok = checks.every(Boolean) && pw === confirm;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) { setError("Missing token."); return; }
    if (!ok) return;
    setBusy(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      nav("/login?reset=1", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/assets/booking-bg.jpg')" }} aria-hidden />
      <div className="absolute inset-0 bg-[#0B0B0C]/80 backdrop-blur-sm" aria-hidden />
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="block text-center mb-10">
          <img src="/assets/brand/phantasm-logo.png" alt="Phantasm Limousine Service Inc." className="mx-auto h-16 w-auto" />
        </Link>
        <form onSubmit={onSubmit} data-testid="reset-form" className="backdrop-blur-2xl bg-[#121214]/80 border border-white/10 p-10 shadow-2xl">
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-3">Account Recovery</div>
          <h1 className="font-serif text-3xl text-white font-semibold tracking-tight mb-8">Set a new password</h1>
          <div className="space-y-6">
            <div>
              <label className="block text-xs uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">New Password</label>
              <input
                type="password"
                required
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="reset-new"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-white focus:outline-none text-base"
                autoComplete="new-password"
              />
              <ul className="mt-3 space-y-1 text-[11px] tracking-wide">
                {[["At least 8 characters", checks[0]],["Includes a letter", checks[1]],["Includes a number", checks[2]]].map(([l, o]) => (
                  <li key={l} className={`flex items-center gap-2 ${o ? "text-white" : "text-[#8E9AA6]"}`}>
                    <span className={`inline-block w-2 h-2 rounded-full ${o ? "bg-white" : "bg-[#8E9AA6]/40"}`} />{l}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">Confirm Password</label>
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-white focus:outline-none text-base"
                autoComplete="new-password"
              />
              {confirm && (
                <div className={`mt-2 text-[11px] ${pw === confirm ? "text-white" : "text-red-400"}`}>
                  {pw === confirm ? "Passwords match." : "Passwords do not match."}
                </div>
              )}
            </div>
          </div>
          {error && <div data-testid="reset-error" className="mt-5 text-xs text-red-400">{error}</div>}
          <button
            type="submit"
            disabled={busy || !ok}
            data-testid="reset-submit-btn"
            className="w-full mt-10 bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50 px-8 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
          >
            {busy ? "Updating…" : "Reset password"}
          </button>
        </form>
      </div>
    </div>
  );
}
